import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { To } from 'react-router';
import styled from 'styled-components';

import { Navigate } from '@sorare/routing';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { NoIndex } from '@sorare/core/src/contexts/seo';

import { WatchlistItem } from '#src/components/watchlists/WatchlistItem/index.tsx';

import type { PublicWatchlists_watchlist } from './__generated__/index.graphql.ts';

const StyledFullWidthContainer = styled(FullWidthContainer)`
  padding-top: var(--triple-unit);
  padding-bottom: var(--triple-unit);
`;

const ItemsContainer = styled(Vertical).attrs({ gap: 1 })`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  column-gap: var(--double-unit);
  row-gap: var(--triple-unit);
`;

type Props = {
  publicWatchlists: PublicWatchlists_watchlist[];
  redirectTo: To;
  redirectIfEmpty?: boolean;
  infiniteScrollNode?: React.ReactNode;
  loading?: boolean;
  hideOwner?: boolean;
  title?: React.ReactNode;
};

export const PublicWatchlists = ({
  publicWatchlists,
  redirectTo,
  redirectIfEmpty,
  infiniteScrollNode,
  loading,
  hideOwner,
  title,
}: Props) => {
  const hasPublicWatchlist = !loading && (publicWatchlists.length || 0) > 0;

  if (loading && !publicWatchlists.length) {
    return <LoadingIndicator fullHeight />;
  }

  if (!hasPublicWatchlist && redirectIfEmpty) {
    return <Navigate to={redirectTo} />;
  }

  return (
    <>
      <NoIndex />
      <StyledFullWidthContainer>
        <Vertical gap={3}>
          {title}
          <ItemsContainer>
            {publicWatchlists.map(watchlist => (
              <WatchlistItem
                key={watchlist.slug}
                watchlist={watchlist}
                hideOwner={hideOwner}
              />
            ))}
          </ItemsContainer>
        </Vertical>
        {infiniteScrollNode}
      </StyledFullWidthContainer>
    </>
  );
};

PublicWatchlists.fragments = {
  watchlist: gql`
    fragment PublicWatchlists_watchlist on Watchlist {
      slug
      ...WatchlistItem_watchlist
    }
    ${WatchlistItem.fragments.watchlist}
  ` as TypedDocumentNode<PublicWatchlists_watchlist>,
};
